import * as Yup from 'yup';
import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';

import { useCustomFields } from 'src/hooks/use-custom-fields';

import { useTranslate } from 'src/locales';
import { ApplicationStatus } from 'src/services/applications/applications.types';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { generateUpdateProfileError } from 'src/services/applications/applications.utils';
import { useGetApplicationByIdQuery } from 'src/services/applications/applications.service';

import Iconify from 'src/components/iconify';
import FormProvider, { RHFTextField } from 'src/components/hook-form';

import { useApplicationContactSave } from './use-application-contact-save';

// ----------------------------------------------------------------------

type Props = {
  applicationId: string;
};

/**
 * The Contact tab of both application quick views: the application's email and phone, and the
 * candidate record's custom fields, since applications have none of their own. Saving fills an
 * email or phone the candidate record lacks, and asks before replacing a different one.
 */
export default function ApplicationContact({ applicationId }: Props) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  const { currentData: application } = useGetApplicationByIdQuery(applicationId, { skip: !applicationId });

  const orgCandidateId = application?.org_candidate?.id;
  const readOnly = application?.status === ApplicationStatus.ARCHIVED;

  const customFields = useCustomFields({
    entityType: CustomFieldEntityType.CANDIDATE,
    entityId: orgCandidateId,
  });

  // Custom fields belong to the candidate record, so there is nothing to show without one.
  const showCustomFields = Boolean(orgCandidateId) && customFields.hasCustomFields;

  const { save, saving, dialog } = useApplicationContactSave();

  const ContactSchema = Yup.object().shape({
    email: Yup.string().email(t('validation.email_format')),
    phone_number: Yup.string(),
  });

  const methods = useForm({
    resolver: yupResolver(ContactSchema),
    defaultValues: { email: '', phone_number: '' },
  });

  const { reset, handleSubmit } = methods;

  // Keyed on the values rather than the application object, so a refetch for anything else (a
  // rating, a note) does not wipe what the recruiter is typing. The phone arrives as one
  // international number, which the backend splits again on save.
  const email = application?.attached_profile?.email ?? '';
  const phone = application?.attached_profile?.phone_number ?? '';
  useEffect(() => {
    reset({ email, phone_number: phone });
  }, [email, phone, reset]);

  const onSubmit = handleSubmit(async (data) => {
    if (showCustomFields && !customFields.validate().isValid) return;

    try {
      await save({
        applicationId,
        orgCandidateId,
        data: {
          email: data.email || undefined,
          phone_number: data.phone_number || undefined,
          // The full set: the backend replaces every value, so a field left out is cleared.
          custom_fields: showCustomFields ? customFields.getData() : undefined,
        },
      });

      enqueueSnackbar(t('applications.contact.api.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(generateUpdateProfileError(error), { variant: 'error' });
    }
  });

  return (
    <>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Grid container spacing={2}>
          <Grid xs={12}>
            <RHFTextField
              name="email"
              disabled={readOnly}
              label={t('common.form_labels.email')}
              InputProps={{ startAdornment: <Iconify icon="mdi:email-outline" sx={{ mr: 1 }} /> }}
            />
          </Grid>

          <Grid xs={12}>
            <RHFTextField
              name="phone_number"
              disabled={readOnly}
              label={t('account.profile.form.phone.label')}
              InputProps={{ startAdornment: <Iconify icon="solar:phone-linear" sx={{ mr: 1 }} /> }}
            />
          </Grid>

          {showCustomFields && !readOnly && customFields.renderCustomFields}

          {showCustomFields && readOnly && customFields.getFields().map((field) => (
            <Grid xs={12} key={field.label}>
              <TextField fullWidth disabled label={field.label} value={field.value} />
            </Grid>
          ))}
        </Grid>

        {!readOnly && (
          <Stack spacing={1.5} sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t('applications.contact.helper')}
            </Typography>

            <LoadingButton type="submit" variant="contained" loading={saving}>
              {t('common.save')}
            </LoadingButton>
          </Stack>
        )}
      </FormProvider>

      {dialog}
    </>
  );
}
